import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  CREDENTIAL_TOKEN_STATE_KEY,
  type CredentialTokenNavState
} from './constants'

/**
 * Reads a one-time credential token from router state at mount and
 * exposes helpers for the detail page:
 *
 *   • `revealedToken` — the fresh token from create/rotate, or `null`.
 *     Populated only on the load that arrives with router state; a reload
 *     starts empty, matching the "show only once" security model.
 *   • `setRevealedToken` — set when a rotate/refresh on the same page
 *     returns a new token.
 *   • `clearRevealed` — hide the banner AND scrub the token from history
 *     via `navigate(..., { replace, state: null })` so a browser Back +
 *     Forward doesn't re-reveal it.
 */
export const useCredentialTokenReveal = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [revealedToken, setRevealedToken] = useState<string | null>(() => {
    const state = location.state as CredentialTokenNavState | null

    return state?.[CREDENTIAL_TOKEN_STATE_KEY] ?? null
  })

  const clearRevealed = () => {
    setRevealedToken(null)
    const state = location.state as CredentialTokenNavState | null
    if (state?.[CREDENTIAL_TOKEN_STATE_KEY]) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }

  return { revealedToken, setRevealedToken, clearRevealed }
}
